import { Children, useState, useCallback } from 'react';
import {
    Box,
    Heading,
    Stack,
    Button,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    Flex,
    Spacer,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import { Card } from 'components/v4/Card';
import SectionHandler from './SectionHandler';
import {
    breadCrumbStyling,
    headingStyling,
    subHeadingStyling,
} from './formStyles';
import { customTabStyles, selectedTabStyles } from '../../../constants';
import { useNavigate, Link } from 'react-router-dom';
import useAutoBackOnManagingChange from 'hooks/useAutoBackOnManagingChange';

const FormBase = ({
    children,
    breadCrumbTrail,
    header,
    initialValues,
    finalFormSubmit,
    subHeader,
    subHeaderComponent,
    noBackBtn,
    ufBackBtn,
    ufSaveBtn,
    backLink,
    extraCards,
    extraButton,
    sideDrawer,
    customClick = false,
    onBack,
    backText = 'Back',
    subTitleHeader = false,
    subTitleHeaderComponent,
    deleteButton,
    isSubmitDisabled,
    isLoading = false,
    enableReinitialize = false,
    tabMode = false,
    defaultTabIndex = 0,
    hideSubmitButton = false,
    hideSaveButton = false,
    showSubmitButton = true,
    autoBackOnManagingChange = true,
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const navigate = useNavigate();
    const [tabIndex, setTabIndex] = useState(defaultTabIndex);

    const navBack = useCallback(() => {
        if (typeof backLink === 'function') {
            backLink();
        } else if (Array.isArray(backLink)) {
            navigate(backLink[0], backLink[1]);
        } else {
            navigate(backLink);
        }
    }, [backLink, navigate]);

    const hasBackAction = customClick
        ? typeof onBack === 'function'
        : Boolean(backLink);

    const handleAutoBack = useCallback(() => {
        if (customClick && typeof onBack === 'function') {
            onBack();
            return;
        }
        navBack();
    }, [customClick, navBack, onBack]);

    useAutoBackOnManagingChange({
        enabled: autoBackOnManagingChange && hasBackAction,
        onBack: handleAutoBack,
    });

    const renderContent = (formProps) => {
        if (tabMode) {
            const tabs =
                typeof children === 'function' ? children(formProps) : children;

            if (!Array.isArray(tabs)) {
                console.error(
                    'In tabMode, children must return an array of tab objects',
                );
                return null;
            }

            return (
                <>
                    {subHeader && (
                        <Flex
                            sx={{
                                paddingBottom: '15px',
                                paddingTop: '25px',
                            }}>
                            <Heading sx={subHeadingStyling}>
                                {subHeader}
                            </Heading>
                            <Spacer />
                            <Box paddingRight="34px">{subHeaderComponent}</Box>
                        </Flex>
                    )}
                    <Tabs index={tabIndex} onChange={setTabIndex} isLazy>
                        <TabList borderBottom={0} mb="10px">
                            {tabs.map((tab, index) => (
                                <Tab
                                    key={index}
                                    sx={customTabStyles}
                                    _selected={selectedTabStyles}>
                                    {tab.label}
                                </Tab>
                            ))}
                        </TabList>
                        <TabPanels>
                            {tabs.map((tab, index) => (
                                <TabPanel key={index} px={0} py={0}>
                                    <Card
                                        borderRadius={15}
                                        padding="30px"
                                        maxWidth={895}
                                        ml={0}
                                        bg={darkMode ? 'dark.tcap' : 'white'}>
                                        {typeof tab.content === 'function'
                                            ? tab.content(formProps)
                                            : tab.content}
                                    </Card>
                                </TabPanel>
                            ))}
                        </TabPanels>
                    </Tabs>
                </>
            );
        } else {
            // Normal mode: render children as sections
            if (!children || typeof children !== 'function') {
                return null;
            }

            const childrenElements = children(formProps);
            const childrenCount = Children.count(childrenElements);

            return (
                <Card
                    borderRadius={15}
                    padding="15px 30px"
                    maxWidth={895}
                    ml={0}
                    bg={darkMode ? 'dark.tcap' : null}>
                    <Stack sx={{ margin: '0 !important' }}>
                        {subHeader && (
                            <Flex
                                sx={{
                                    paddingBottom: '15px',
                                    paddingTop: '25px',
                                }}>
                                <Heading sx={subHeadingStyling}>
                                    {subHeader}
                                </Heading>
                                <Spacer />
                                <Box paddingRight="34px">
                                    {subHeaderComponent}
                                </Box>
                            </Flex>
                        )}
                        {Children.map(childrenElements, (child, index) => (
                            <SectionHandler
                                key={index}
                                index={index}
                                child={child}
                                count={childrenCount}
                            />
                        ))}
                    </Stack>
                </Card>
            );
        }
    };

    return (
        <Box>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        {Array.isArray(crumb.path) ? (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path[0]}
                                state={crumb.path[1]}>
                                {crumb.label}
                            </BreadcrumbLink>
                        ) : (
                            <BreadcrumbLink
                                as={Link}
                                to={
                                    typeof crumb.path === 'function'
                                        ? '#'
                                        : crumb.path
                                }
                                onClick={() =>
                                    typeof crumb.path === 'function'
                                        ? crumb.path()
                                        : null
                                }>
                                {crumb.label}
                            </BreadcrumbLink>
                        )}
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <Stack>
                {typeof header === 'string' ? (
                    <Heading sx={headingStyling}>{header}</Heading>
                ) : (
                    header
                )}
                {subTitleHeader && <>{subTitleHeaderComponent}</>}
            </Stack>
            <Formik
                initialValues={initialValues}
                enableReinitialize={enableReinitialize}
                onSubmit={async (value, actions) => {
                    try {
                        await finalFormSubmit(value, actions);
                    } finally {
                        actions.setSubmitting(false);
                    }
                }}
                validateOnChange>
                {(formProps) => {
                    const { handleSubmit, isSubmitting } = formProps;
                    const submitting = isLoading || isSubmitting;
                    return (
                        <form onSubmit={handleSubmit}>
                            <Stack spacing={30}>
                                {renderContent(formProps)}
                                {!!extraCards &&
                                    extraCards.map((item, index) => (
                                        <Card
                                            key={index}
                                            borderRadius={15}
                                            maxWidth={895}
                                            ml={0}
                                            bg={darkMode ? 'dark.tcap' : null}>
                                            {item}
                                        </Card>
                                    ))}
                                <Stack
                                    direction="row"
                                    maxWidth={895}
                                    sx={{
                                        paddingBottom: sideDrawer ? 3 : 10,
                                        marginLeft: sideDrawer && 0,
                                        marginTop: sideDrawer && 25,
                                        paddingTop: sideDrawer && 3,
                                        position: sideDrawer
                                            ? 'sticky'
                                            : 'static',
                                        left: sideDrawer ? 0 : 'auto',
                                        bottom: sideDrawer ? -35 : 'auto',
                                        backgroundColor:
                                            sideDrawer &&
                                            (darkMode
                                                ? 'var(--chakra-colors-dark-bgDark)'
                                                : '#F5F5F5'),
                                        zIndex: 1,
                                        width: '100%',
                                    }}>
                                    <div
                                        className="edit-form-buttons"
                                        style={{
                                            border: 'none',
                                            textAlign: 'center',
                                            justifyContent: 'center',
                                        }}>
                                        {!hideSubmitButton &&
                                            !hideSaveButton &&
                                            showSubmitButton && (
                                                <Button
                                                    type="submit"
                                                    data-walkthroughid={
                                                        ufSaveBtn
                                                    }
                                                    className="h-10 min-w-[5rem] font-semibold disabled:cursor-not-allowed"
                                                    isDisabled={
                                                        isSubmitDisabled ||
                                                        submitting
                                                    }
                                                    isLoading={submitting}
                                                    loadingText="Saving"
                                                    sx={{ width: 142 }}>
                                                    Save
                                                </Button>
                                            )}
                                        {!noBackBtn && (
                                            <div
                                                style={{
                                                    marginLeft:
                                                        !hideSubmitButton &&
                                                        !hideSaveButton &&
                                                        showSubmitButton
                                                            ? 30
                                                            : 0,
                                                    display: 'inline',
                                                }}>
                                                <Button
                                                    className="secondary h-10 min-w-[5rem] font-semibold"
                                                    variant="outline"
                                                    background="white"
                                                    sx={{ width: 142 }}
                                                    data-walkthroughid={
                                                        ufBackBtn
                                                    }
                                                    onClick={
                                                        customClick
                                                            ? onBack
                                                            : navBack
                                                    }>
                                                    {backText}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                            flex: 1,
                                        }}>
                                        {extraButton}
                                    </div>
                                    {deleteButton && (
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                flex: 1,
                                            }}>
                                            <Button
                                                variant="solid"
                                                colorScheme="red"
                                                sx={{ width: 142 }}
                                                isLoading={
                                                    deleteButton.isLoading
                                                }
                                                onClick={() =>
                                                    deleteButton.onDeleteOpen()
                                                }
                                                data-walkthroughid={
                                                    deleteButton.dataWalkthroughId
                                                }>
                                                Delete
                                            </Button>
                                        </div>
                                    )}
                                </Stack>
                            </Stack>
                        </form>
                    );
                }}
            </Formik>
        </Box>
    );
};

export default FormBase;
